/**
 * navCard
 *
 * The one nav-card row shape: a `.note-row` card whose content column carries a
 * title and an optional one-line snippet, with a right rail (status chip, date,
 * or nothing) and a chevron revealed on hover. Every artifact a page links out
 * to wears it — open threads on /discovery and in session detail, and session
 * detail's experiments and partner notes — so a reader meets one card and learns
 * it once.
 *
 * Visual only. Each caller wires its own click delegation, because the surfaces
 * open their targets through different plumbing (`right-panel:drill` when the
 * panel can take another layer, a deep link when it can't).
 *
 * Styling: `components/nav-card.css` (`.nav-card`, `.nav-card__title`,
 * `.nav-card__snippet`). Per-kind classes ride alongside those for anything a
 * single kind needs — never for the shared shape itself.
 *
 * @module components/navCard
 */

import { escapeHtml } from '../core/utils.js';

/** The chevron every nav card ends with (hover-revealed by `.note-row__chevron`). */
const CHEVRON_SVG = `
  <svg width="19" height="19" viewBox="0 0 16 16">
    <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  </svg>
`;

/**
 * Serialize a `{ 'data-thread-id': 'x' }` map into escaped HTML attributes.
 * Entries with a null/undefined value are dropped, so a caller can pass an
 * optional attribute without branching at the call site.
 *
 * @param {Object<string, string|number|null|undefined>} attrs
 * @returns {string} Attribute string, leading space included, or ''
 */
function serializeAttrs(attrs) {
  return Object.entries(attrs)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([name, value]) => ` ${name}="${escapeHtml(String(value))}"`)
    .join('');
}

/**
 * Render one nav card.
 *
 * @param {Object} params
 * @param {string} params.title - Plain (unescaped) title text; escaped here
 * @param {Object<string, string|number|null|undefined>} [params.dataAttrs] - Identity
 *   attributes the caller's delegation matches on (e.g. `{ 'data-practice-id': id }`)
 * @param {string} [params.cardClass] - Extra classes on the card element
 * @param {string} [params.titleClass] - Extra classes on the title element
 * @param {string} [params.snippet] - Plain one-line context text; omitted when empty
 * @param {string} [params.snippetClass] - Extra classes on the snippet element
 * @param {string} [params.railHtml] - Pre-rendered right-rail HTML (status chip, date,
 *   tag). Trusted markup — callers escape their own text.
 * @returns {string} HTML string
 */
export function renderNavCard({
  title,
  dataAttrs = {},
  cardClass = '',
  titleClass = '',
  snippet = '',
  snippetClass = '',
  railHtml = ''
}) {
  const snippetText = String(snippet || '').trim();
  const snippetHtml = snippetText
    ? `<p class="session-card__summary nav-card__snippet${snippetClass ? ` ${snippetClass}` : ''}">${escapeHtml(snippetText)}</p>`
    : '';

  return `
    <div class="card session-card nav-card note-row${cardClass ? ` ${cardClass}` : ''}"${serializeAttrs(dataAttrs)}
         role="button" tabindex="0">
      <div class="session-card__content">
        <div class="session-card__title-row">
          <h4 class="session-card__title nav-card__title${titleClass ? ` ${titleClass}` : ''}">${escapeHtml(title)}</h4>
        </div>
        ${snippetHtml}
      </div>
      <span class="note-row__meta">
        ${railHtml}
        <span class="session-card__chevron note-row__chevron" aria-hidden="true">${CHEVRON_SVG}</span>
      </span>
    </div>
  `;
}
